import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import type { AuthUser } from '@/core/auth/types';
import { useMemo, useState, useEffect, useCallback } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function decodeJwtToken(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload) as Record<string, unknown>;
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
}

type UseAuthReturn = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  register: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
};

function useKindeAuthAdapter(): UseAuthReturn {
  const kinde = useKindeAuth();
  const [decodedToken, setDecodedToken] = useState<Record<string, unknown> | null>(null);

  const { login, logout, register, isAuthenticated, isLoading, user: kindeUser, getToken, getIdToken } = kinde;

  useEffect(() => {
    const decodeToken = async () => {
      if (isAuthenticated && !isLoading) {
        try {
          // Use getIdToken() to get ID token which contains user profile claims
          // Access tokens (from getToken()) don't have given_name, family_name, etc.
          const idToken = await getIdToken();
          if (idToken) {
            const decoded = decodeJwtToken(idToken);
            setDecodedToken(decoded);
          }
        } catch (error) {
          console.error('Failed to get/decode ID token:', error);
        }
      }
    };

    decodeToken();
  }, [isAuthenticated, isLoading, getIdToken]);

  // Transform Kinde user to our AuthUser type
  const user = useMemo<AuthUser | null>(() => {
    if (!isAuthenticated || !kindeUser) {
      return null;
    }

    // Extract custom claims from Kinde user object
    // Note: In Kinde SDK v5+, properties are directly on the user object
    const kindeUserAny = kindeUser as Record<string, unknown>;
    const userProps = (decodedToken?.user_properties ?? {}) as Record<string, { v: unknown }>;

    // Use decoded token claims as the authoritative source
    // Custom properties are nested under user_properties with a 'v' key holding the actual value
    const userId = (userProps?.user_id?.v as string) || null;
    const kindeId = (decodedToken?.sub as string) || (kindeUserAny.id as string);
    const role = (userProps?.role?.v as string) || null;
    // Note: Some values might be strings ("true"/"false") instead of booleans
    const onboardingCompleted =
      userProps?.onboarding_completed?.v === true ||
      userProps?.onboarding_completed?.v === 'true';

    // Try multiple sources for the user's name (priority order)
    // Kinde SDK v5 UserProfile uses camelCase: givenName, familyName
    // ID token (OIDC) uses snake_case: given_name, family_name
    let firstName = 'User';
    let lastName: string | undefined;

    if (kindeUserAny.givenName) {
      // 1. Kinde SDK v5 UserProfile (camelCase)
      firstName = kindeUserAny.givenName as string;
      lastName = kindeUserAny.familyName as string | undefined;
    } else if (decodedToken?.given_name) {
      // 2. ID token standard OIDC claims (snake_case)
      firstName = decodedToken.given_name as string;
      lastName = (decodedToken.family_name as string) || undefined;
    } else if (kindeUserAny.given_name) {
      // 3. Legacy/alternative Kinde SDK format (snake_case on user obj)
      firstName = kindeUserAny.given_name as string;
      lastName = kindeUserAny.family_name as string | undefined;
    } else if (kindeUserAny.name) {
      // 4. Kinde 'name' property — split by space
      const nameParts = (kindeUserAny.name as string).split(' ');
      firstName = nameParts[0] || 'User';
      lastName = nameParts.slice(1).join(' ') || undefined;
    } else if (decodedToken?.name) {
      // 5. ID token 'name' claim — split by space
      const nameParts = (decodedToken.name as string).split(' ');
      firstName = nameParts[0] || 'User';
      lastName = nameParts.slice(1).join(' ') || undefined;
    } else if (kindeUser.email) {
      // 6. Last resort: extract name from email (strip alias with +)
      const emailName = kindeUser.email.split('@')[0].split('+')[0];
      const nameParts = emailName
        .split(/[._-]/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1));
      firstName = nameParts[0] || 'User';
      lastName = nameParts.slice(1).join(' ') || undefined;
    }

    // Handle boolean values that might be strings
    const profileCompletedValue = userProps?.profile_completed?.v;
    const profileCompleted =
      profileCompletedValue === true || profileCompletedValue === 'true';

    const authUser: AuthUser = {
      id: userId ?? '',
      kindeId: kindeId ?? '',
      email: kindeUser.email || '',
      firstName,
      lastName,
      role: role as AuthUser['role'],
      profileCompleted: profileCompleted,
      vettingStatus: (userProps?.vetting_status?.v as AuthUser['vettingStatus']) || undefined,
      accountStatus: (userProps?.account_status?.v as string) || undefined,
      onboardingCompleted: onboardingCompleted,
      // Only set profilePhoto if it exists and is not a default Gravatar URL
      profilePhoto:
        kindeUser.picture && !kindeUser.picture.includes('gravatar.com/avatar')
          ? kindeUser.picture
          : undefined,
    };

    // If JWT token has onboardingCompleted=true, clear the sessionStorage cache
    // This means the token has been refreshed with updated claims
    if (authUser.onboardingCompleted && authUser.role === 'CARE_SEEKER') {
      const cached = sessionStorage.getItem('onboardingCompleted');
      if (cached === 'true') {
        sessionStorage.removeItem('onboardingCompleted');
      }
    }

    return authUser;
  }, [isAuthenticated, kindeUser, decodedToken]);

  // Get access token for API calls
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const token = await getToken();
      return token || null;
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }, [getToken]);

  // Login with redirect
  const handleLogin = useCallback(() => {
    login();
  }, [login]);

  // Register with redirect
  const handleRegister = useCallback(() => {
    register();
  }, [register]);

  // Logout with redirect
  const handleLogout = useCallback(() => {
    localStorage.removeItem('chat_session_id');
    logout();
  }, [logout]);

  return {
    user,
    isAuthenticated,
    isLoading,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    getAccessToken,
  };
}

export function useAuth(): UseAuthReturn {
  return useKindeAuthAdapter();
}
